// AI Service - Uses Supabase Edge Functions for secure API access
// SECURITY: Never expose API keys in frontend code

import { RiskLevel, Category } from '../types';
import { getSupabase, extractFunctionError, NETWORK_ERROR_MESSAGE } from './supabase';
import { logger } from '../utils/logger';
/* 上限到達ケースのタイトルは判定側と同じ定数を使う。
   ここに文字列を直書きしていたため、caseClassification.isLimitExceededCase の
   完全一致判定と食い違うと、上限到達のダミー案件が管理画面のKPI
   （未対応High・改善実施率・滞留日数）に混入する。エラーは出ない。 */
import { LIMIT_EXCEEDED_TITLE } from '../utils/caseClassification';
import { logInfo, logError, logDebug, logWarn } from './clientLogger';

export interface AIAnalysisResult {
  title: string;
  formattedBody: string;
  riskLevel: RiskLevel;
  category1: Category;
  category2: string;
  lesson: string;
}

export interface TranscriptionResult {
  text: string;
  success: boolean;
  error?: string;
}

// Mock responses for demo mode (when Edge Functions are unavailable)
const MOCK_RESPONSES: AIAnalysisResult[] = [
  {
    title: '高所作業時の安全帯不使用',
    formattedBody: '【状況】3階足場にて従業員が安全帯フックを掛けずに移動していた。\n【行動】直ちに声を掛け、フックの使用を徹底させた。\n【結果】従業員は直ちに是正し、安全に作業を再開した。',
    riskLevel: RiskLevel.HIGH,
    category1: Category.MAN,
    category2: '高所作業',
    lesson: '慣れによる不安全行動への相互注意が必要。声掛けの習慣化が重要である。'
  },
  {
    title: '通路への資材放置による転倒リスク',
    formattedBody: '【状況】第2通路にパレットが放置されており、つまづく危険性があった。\n【行動】現場責任者に報告し、資材の移動を依頼した。\n【結果】30分以内に資材が撤去され、通路が確保された。',
    riskLevel: RiskLevel.MEDIUM,
    category1: Category.MATERIAL,
    category2: '整理整頓',
    lesson: '5Sの徹底と定期的な通路点検の実施が必要である。'
  },
  {
    title: 'フォークリフト後退時の確認徹底',
    formattedBody: '【状況】フォークリフト後退時、運転者が確認呼称を行っていた。\n【行動】良好事例として記録した。\n【結果】他の作業員への水平展開の好例となった。',
    riskLevel: RiskLevel.LOW,
    category1: Category.MAN,
    category2: '重機操作',
    lesson: '確認呼称の習慣化が安全行動の定着に効果的である。'
  }
];

// Helper functions
function mapRiskLevel(value: string): RiskLevel {
  const normalized = value?.toLowerCase();
  if (normalized === 'high') return RiskLevel.HIGH;
  if (normalized === 'medium') return RiskLevel.MEDIUM;
  return RiskLevel.LOW;
}

function mapCategory(value: string): Category {
  if (value?.includes('Man') || value?.includes('人')) return Category.MAN;
  if (value?.includes('Machine') || value?.includes('機械')) return Category.MACHINE;
  if (value?.includes('Material') || value?.includes('材料')) return Category.MATERIAL;
  return Category.METHOD;
}

function simulateDelay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Analyze a safety report using AI via Edge Function
 * Falls back to demo mode if Edge Function is unavailable
 */
// Store last debug info for UI display
let lastDebugInfo: Record<string, unknown> = {};
export function getLastDebugInfo(): Record<string, unknown> {
  return lastDebugInfo;
}

export async function analyzeSafetyReport(
  text: string,
  transcribedText?: string,
  organizationId?: string,
  equipmentInfo?: string,
  /* 作業員(QR経由)の認可に使う。作業員はセッションを持たないので、
     これを送らないと Edge Function 側が 401 を返し、AI分析が
     モック応答に落ちる（2026-08-20 に本番で発生）。
     管理画面からの操作では undefined のままでよい（メンバー検証に回る）。 */
  qrToken?: string
): Promise<AIAnalysisResult> {
  const supabase = getSupabase();

  // Check environment variables
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  // Detailed debug logging
  const debugInfo = {
    hasText: !!text,
    textLength: text?.length,
    hasTranscribedText: !!transcribedText,
    organizationId: organizationId || 'MISSING',
    hasEquipmentInfo: !!equipmentInfo,
    hasSupabase: !!supabase,
    hasSupabaseUrl: !!supabaseUrl,
    hasSupabaseKey: !!supabaseKey,
    supabaseUrlPrefix: supabaseUrl ? supabaseUrl.substring(0, 30) + '...' : 'null',
    timestamp: new Date().toISOString()
  };

  // Store for UI access
  lastDebugInfo = debugInfo;

  // Log to persistent storage
  logInfo('analyzeSafetyReport called', debugInfo, 'aiService');

  // If no Supabase client or no organization ID, use demo mode
  if (!supabase || !organizationId) {
    const reason = !supabase
      ? `supabase is null (url: ${!!supabaseUrl}, key: ${!!supabaseKey})`
      : 'organizationId is missing';

    // Store reason for UI
    lastDebugInfo.demoModeReason = reason;
    lastDebugInfo.mode = 'DEMO';

    logWarn('DEMO MODE ACTIVATED', { reason }, 'aiService');

    await simulateDelay(1500);
    return MOCK_RESPONSES[Math.floor(Math.random() * MOCK_RESPONSES.length)];
  }

  lastDebugInfo.mode = 'PRODUCTION';
  logInfo('Edge Function call starting', { organizationId }, 'aiService');

  try {
    // Call the Edge Function
    logDebug('Invoking Edge Function', { functionName: 'genbasafeai_analyze-report' }, 'aiService');

    const { data, error } = await supabase.functions.invoke('genbasafeai_analyze-report', {
      body: {
        text,
        transcribedText,
        organizationId,
        qrToken,
        equipmentInfo
      }
    });

    logInfo('Edge Function response received', { hasData: !!data, hasError: !!error }, 'aiService');

    if (error) {
      // Extract detailed error info from FunctionsError
      // NOTE: Supabase JS SDK のバージョンによって context の構造が異なる。
      //   v2.45 未満: { body: string }
      //   v2.45 以降: Response オブジェクト相当（body は ReadableStream で同期参照不可、status を直接持つ）
      // どちらの形でも LIMIT_EXCEEDED を取りこぼさないよう、複数経路で検出する。
      const errorContext = (error as { context?: { body?: string; status?: number } }).context;
      let errorBody: { error?: string; code?: string } | null = null;

      // Try to parse error body from context (旧形式)
      if (errorContext?.body && typeof errorContext.body === 'string') {
        try {
          errorBody = JSON.parse(errorContext.body);
        } catch {
          // Body is not JSON
        }
      }

      // Resolve HTTP status from any of the locations the SDK may expose it
      const statusFromCtx = errorContext?.status;
      const statusFromErr = (error as { status?: number }).status;
      const httpStatus = statusFromCtx ?? statusFromErr;

      const errorDetails = {
        message: error.message || String(error),
        name: error.name,
        context: errorContext,
        status: httpStatus,
        errorBody
      };
      logError('Edge Function error', errorDetails, 'aiService');
      lastDebugInfo.edgeFunctionError = JSON.stringify(errorDetails);

      // Check for LIMIT_EXCEEDED via multiple signals:
      //   (a) errorBody.code === 'LIMIT_EXCEEDED' (旧 SDK: body parse できた場合)
      //   (b) errorBody.error contains 'limit exceeded' (caps insensitive)
      //   (c) HTTP 429 (新 SDK で body が読めなくても status は取れる)
      //   (d) error.message に 'limit' か '429' が含まれる (最後の保険)
      const errMsgLower = String(error.message || '').toLowerCase();
      const errorTextLower = String(errorBody?.error || '').toLowerCase();
      const isLimitExceeded =
        errorBody?.code === 'LIMIT_EXCEEDED' ||
        errorTextLower.includes('limit exceeded') ||
        httpStatus === 429 ||
        errMsgLower.includes('limit exceeded') ||
        errMsgLower.includes('429');

      if (isLimitExceeded) {
        lastDebugInfo.limitExceeded = true;
        lastDebugInfo.mode = 'LIMIT_EXCEEDED';
        logWarn('LIMIT_EXCEEDED detected', {
          via: errorBody?.code ? 'errorBody.code' :
               errorTextLower.includes('limit exceeded') ? 'errorBody.error' :
               httpStatus === 429 ? 'httpStatus' :
               'errorMessage',
          httpStatus
        }, 'aiService');
        throw new Error('LIMIT_EXCEEDED: 今月のAI分析利用上限に達しました。管理者に連絡するか、プランのアップグレードをご検討ください。');
      }

      throw error;
    }

    if (!data) {
      logError('Empty response from Edge Function', {}, 'aiService');
      throw new Error('Empty response from Edge Function');
    }

    logInfo('Edge Function SUCCESS', { title: data.title }, 'aiService');
    lastDebugInfo.edgeFunctionSuccess = true;

    // Map the response to our typed result
    return {
      title: data.title || '報告',
      formattedBody: data.formattedBody || text,
      riskLevel: mapRiskLevel(data.riskLevel),
      category1: mapCategory(data.category1),
      category2: data.category2 || 'その他',
      lesson: data.lesson || ''
    };

  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    const isLimitExceeded = errorMessage.includes('LIMIT_EXCEEDED');

    logError('AI Analysis EXCEPTION', {
      error: errorMessage,
      type: typeof error,
      isLimitExceeded
    }, 'aiService');

    lastDebugInfo.exception = errorMessage;

    // Check if it's a LIMIT_EXCEEDED error
    if (isLimitExceeded) {
      lastDebugInfo.mode = 'LIMIT_EXCEEDED';
      lastDebugInfo.limitExceeded = true;
    } else {
      lastDebugInfo.mode = 'FALLBACK';
    }

    // Return graceful fallback with guaranteed non-empty values
    const fallbackBody = text?.trim() || transcribedText?.trim() || '報告内容が入力されました。';
    const lessonText = isLimitExceeded
      ? '⚠️ 今月のAI分析利用上限に達しました。管理者に連絡するか、プランのアップグレードをご検討ください。'
      : '詳細な分析ができませんでした。内容をご確認ください。';

    return {
      title: isLimitExceeded ? LIMIT_EXCEEDED_TITLE : '報告内容',
      formattedBody: fallbackBody,
      riskLevel: RiskLevel.MEDIUM,
      category1: Category.METHOD,
      category2: 'その他',
      lesson: lessonText
    };
  }
}

/**
 * Transcribe audio using AI via Edge Function
 * Falls back to demo mode if Edge Function is unavailable
 */
export async function transcribeAudio(
  audioBlob: Blob,
  organizationId?: string,
  /* analyzeSafetyReport と同じ理由。音声も作業員が匿名で使う経路。 */
  qrToken?: string
): Promise<TranscriptionResult> {
  const supabase = getSupabase();

  // If no Supabase client or no organization ID, use demo mode
  if (!supabase || !organizationId) {
    logger.log('Running in demo mode - returning mock transcription');
    await simulateDelay(1000);
    return {
      text: '（デモモード：音声入力のサンプルテキストです。実際の運用ではここに音声認識結果が表示されます。）',
      success: true
    };
  }

  try {
    // Convert blob to base64 for Edge Function
    const base64Audio = await blobToBase64(audioBlob);

    // Call the Edge Function
    const { data, error } = await supabase.functions.invoke('genbasafeai_transcribe-audio', {
      body: {
        audio: base64Audio,
        mimeType: audioBlob.type || 'audio/webm',
        organizationId,
        qrToken
      }
    });

    if (error) {
      logger.error('Edge Function error:', error);
      /* error.message は SDK の英文（"...non-2xx status code"）。
         そのまま throw すると日本語 UI に英語が出る。本文の日本語を使う。 */
      const ex = await extractFunctionError(error);
      throw new Error(ex.message ?? (ex.reached ? '音声の文字起こしに失敗しました。' : NETWORK_ERROR_MESSAGE));
    }

    // Check if the response indicates success
    if (data?.success === false) {
      logger.error('Transcription failed:', data?.error);
      return {
        text: '',
        success: false,
        error: data?.error || '音声の文字起こしに失敗しました'
      };
    }

    // Ensure we have text
    if (!data?.text) {
      return {
        text: '',
        success: false,
        error: '音声からテキストを取得できませんでした'
      };
    }

    return {
      text: data.text,
      success: true
    };

  } catch (error) {
    logger.error('Transcription Error:', error);
    return {
      text: '',
      success: false,
      error: error instanceof Error ? error.message : '音声の文字起こしに失敗しました'
    };
  }
}

// Helper: Convert Blob to base64 string
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix to get pure base64
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
